import type { Market } from "@spree/sdk";
import {
  type DomainMarket,
  getDefaultDomain,
  getDomainMarkets,
} from "@/lib/domain/host-map";

/**
 * The storefront domain that serves a visitor's country, when it isn't the one
 * they're on. Driven by the Spree markets (`country_codes`), so moving a country
 * between markets in the admin changes the suggestion with no code change:
 *
 *   visitor country → the market claiming it → the domain whose market country
 *   belongs to that same market. A country no market claims → the default
 *   (Rest-of-World) domain.
 *
 * Returns null when the country or current domain is unknown, when market data
 * is missing (a failed fetch must not send everyone to the default store), or
 * when the visitor is already on the right domain.
 */
export function suggestStore(
  country: string | undefined,
  markets: Market[],
  current: DomainMarket | undefined,
): DomainMarket | null {
  if (!country || !current || markets.length === 0) return null;

  const marketFor = (iso: string) =>
    markets.find((m) =>
      m.country_codes.some((c) => c.toUpperCase() === iso.toUpperCase()),
    );

  const visitorMarket = marketFor(country);
  const target =
    (visitorMarket &&
      getDomainMarkets().find(
        (d) => !d.default && marketFor(d.country)?.id === visitorMarket.id,
      )) ||
    getDefaultDomain();

  if (!target || target.host === current.host) return null;
  return target;
}
